// src/lib/settings/types.ts
//
// Application-level settings (not user preferences). Backed
// by localStorage in v1; Postgres `app_settings` table in
// wp12a. Per-key schema validation + history of changes
// lives in the store (lib/settings/useSettings.tsx).
//
// Why split "settings" from "preferences":
//   - Settings are *organizational* config (GLS API URL, AWS
//     region, SMTP relay). The same value is shared by every
//     user.
//   - Preferences are *per-user* (theme, language, default
//     landing page). See lib/preferences (future).
//
// Settings live at /admin/settings.

export type SettingsCategory =
  | "integrations"   // GLS, FileMaker, bank reconciliation, etc.
  | "identity"        // SSO config (Keycloak URL, realm, client id)
  | "sales"           // default sales person, monthly quota, etc.
  | "audit"          // retention period, mask rules, etc.
  | "sync";           // sync application config (frequency, schedule, retry, DLQ)

export interface SettingsEntry {
  key: string;
  category: SettingsCategory;
  label: string;
  description: string;
  // Free-form value. Each SettingsEntry has its own renderer
  // in the UI (input/select/switch/json editor).
  value: string;
  // Sensitive keys are masked in the UI and logged as
  // "*** redacted ***" in the audit trail.
  sensitive: boolean;
  // Last-edited metadata. Not editable from the UI.
  lastEditedAt?: string;
  lastEditedBy?: string;
}

export const SETTINGS_CATEGORIES: Array<{
  key: SettingsCategory;
  label: string;
  description: string;
}> = [
  {
    key: "integrations",
    label: "Integrations",
    description:
      "External services BENCHMARK talks to: GLS for shipping, FileMaker for accounting, AWS regions, SMTP for transactional email.",
  },
  {
    key: "identity",
    label: "Identity",
    description:
      "Single sign-on (Keycloak) configuration. URL + realm + client id. Used by the BENCHMARK app to validate OIDC tokens.",
  },
  {
    key: "sales",
    label: "Sales",
    description:
      "Sales-team defaults: monthly quota per sales person, default commission tier, default car benefit.",
  },
  {
    key: "audit",
    label: "Audit",
    description:
      "Audit log retention period, PII masking rules, immutable export cadence.",
  },
  {
    key: "sync",
    label: "Sync application",
    description:
      "Per docs/proposals/filemaker-sync.md: the on-prem sync agent + AWS fmmirror-mcp + ben-fm-writeback collect their schedule, retry, and dead-letter policy from this section. Historical task logs live under /admin/filemaker-sync.",
  },
];

// ----- canonical seed values -----
// These are the canonical BENCHMARK defaults. The store may
// override them per environment via the admin Settings UI.
export const SEED_SETTINGS: SettingsEntry[] = [
  // Integrations
  {
    key: "gls.api_base_url",
    category: "integrations",
    label: "GLS API base URL",
    description:
      "GLS Business API endpoint. The shop-side sync agent + the AWS fmmirror-mcp both hit this URL.",
    value: "https://api.gls-group.eu/v1",
    sensitive: false,
  },
  {
    key: "gls.account_number",
    category: "integrations",
    label: "GLS account number",
    description:
      "BENCHMARK's GLS account id, used as the `customer` field on label creation.",
    value: "AV-2026-001",
    sensitive: false,
  },
  {
    key: "gls.api_key",
    category: "integrations",
    label: "GLS API key",
    description:
      "Bearer token for the GLS Business API. Rotated every 90 days via the ops/sync/filemaker/ binary.",
    value: "gls-prod-redacted-2026-07-14",
    sensitive: true,
  },
  {
    key: "filemaker.server_url",
    category: "integrations",
    label: "FileMaker Server URL",
    description:
      "Internal URL on the shop LAN. Used by avc-fm-sync-agent. Format: https://host/fmi/data/v1",
    value: "https://filemaker.benchmark-av.local/fmi/data/v1",
    sensitive: false,
  },
  {
    key: "filemaker.db_name",
    category: "integrations",
    label: "FileMaker database name",
    description: "The FileMaker database the sync agent talks to.",
    value: "BENCHMARK_2024",
    sensitive: false,
  },
  {
    key: "aws.primary_region",
    category: "integrations",
    label: "AWS primary region",
    description:
      "Region for the BENCHMARK app's runtime ECS + RDS + S3 stack. Mirror to secondary for prod.",
    value: "eu-central-1",
    sensitive: false,
  },
  {
    key: "smtp.host",
    category: "integrations",
    label: "SMTP host",
    description:
      "Outbound transactional email (order confirmations, password resets, anomaly alerts).",
    value: "smtp.benchmark-av.be",
    sensitive: false,
  },
  {
    key: "smtp.from_address",
    category: "integrations",
    label: "SMTP from address",
    description: "From-address on BENCHMARK-sent emails.",
    value: "no-reply@benchmark-av.be",
    sensitive: false,
  },
  // Identity
  {
    key: "keycloak.url",
    category: "identity",
    label: "Keycloak server URL",
    description:
      "OIDC issuer URL. The BENCHMARK app validates ID tokens against this issuer's JWKS.",
    value: "https://keycloak.benchmark-av.local",
    sensitive: false,
  },
  {
    key: "keycloak.realm",
    category: "identity",
    label: "Keycloak realm",
    description: "BENCHMARK realm name in Keycloak.",
    value: "benchmark-dev",
    sensitive: false,
  },
  {
    key: "keycloak.client_id",
    category: "identity",
    label: "Keycloak client id",
    description:
      "Public OIDC client id (PKCE). The BENCHMARK app uses this in its authorisation-code flow.",
    value: "benchmark-public",
    sensitive: false,
  },
  {
    key: "keycloak.allowed_groups",
    category: "identity",
    label: "Allowed groups",
    description:
      "Comma-separated list of Keycloak group names allowed to sign in. Comma-separated. Adjust when adding new roles.",
    value:
      "bench-staff,bench-approvers,bench-customer,bench-auditors,bench-marketing,bench-sales",
    sensitive: false,
  },
  // Sales
  {
    key: "sales.default_quota_eur",
    category: "sales",
    label: "Default monthly quota (EUR)",
    description:
      "Default monthly net-revenue quota per sales person, used by the sales dashboard until overridden per person.",
    value: "50000",
    sensitive: false,
  },
  {
    key: "sales.default_commission_pct",
    category: "sales",
    label: "Default commission %",
    description:
      "Default commission % applied when no incentive plan is active.",
    value: "3",
    sensitive: false,
  },
  {
    key: "sales.fiscal_year_start",
    category: "sales",
    label: "Fiscal year start (MM-DD)",
    description: "Used by the incentive-plan engine for annual-tier breakpoints.",
    value: "01-01",
    sensitive: false,
  },
  // Audit
  {
    key: "audit.retention_days",
    category: "audit",
    label: "Audit log retention (days)",
    description:
      "Audit log rows are archived to S3 Glacier after this many days. Org-1: 365; Org-2 (prod): 3650.",
    value: "365",
    sensitive: false,
  },
  {
    key: "audit.mask_emails",
    category: "audit",
    label: "Mask emails in audit log",
    description:
      "When true, customer emails in audit entries are stored as `***@***`. Disable only in dev.",
    value: "true",
    sensitive: false,
  },
  // Sync application
  // Per docs/proposals/filemaker-sync.md the on-prem sync agent +
  // fmmirror-mcp + ben-fm-writeback collect their config from
  // here. In production the BENCHMARK app pulls these via the
  // /api/filemaker-sync/config endpoint at agent startup; in v1
  // they live in localStorage and the BENCHMARK app reads them
  // directly.
  {
    key: "sync.tick_interval_seconds",
    category: "sync",
    label: "Tick interval (seconds)",
    description:
      "How often the on-prem sync agent polls FileMaker and the AWS ben-fm-writeback retries pending rows. Default 60s.",
    value: "60",
    sensitive: false,
  },
  {
    key: "sync.schedule_cron",
    category: "sync",
    label: "Daily reconciliation cron",
    description:
      "Cron expression for the daily reconciliation pass (Agent C per docs/proposals/payment-terms-repairs-importer.md). UTC. Default 02:30 nightly.",
    value: "30 2 * * *",
    sensitive: false,
  },
  {
    key: "sync.batch_size",
    category: "sync",
    label: "Records per FileMaker batch",
    description:
      "How many records the on-prem sync agent pulls per FileMaker Data API page. Default 50. Higher = faster but more memory pressure.",
    value: "50",
    sensitive: false,
  },
  {
    key: "sync.retry_max_attempts",
    category: "sync",
    label: "Retry max attempts",
    description:
      "How many times the sync layer retries a transient failure before parking the row in the dead-letter queue. Default 5.",
    value: "5",
    sensitive: false,
  },
  {
    key: "sync.retry_backoff_seconds",
    category: "sync",
    label: "Retry backoff (seconds)",
    description:
      "Exponential backoff base: attempt N waits N * this-many seconds before retrying. Capped at 5 minutes. Default 30.",
    value: "30",
    sensitive: false,
  },
  {
    key: "sync.dlq_after_attempts",
    category: "sync",
    label: "Dead-letter after N attempts",
    description:
      "If retry_max_attempts is exceeded, the row lands in the dead-letter queue and an alert fires. Default 5 (matches retry_max).",
    value: "5",
    sensitive: false,
  },
  {
    key: "sync.alert_channel",
    category: "sync",
    label: "Alert channel",
    description:
      "Where sync anomalies + DLQ events are pushed. Email + Telegram are the supported channels. Default 'telegram'.",
    value: "telegram",
    sensitive: false,
  },
  {
    key: "sync.token_rotation_days",
    category: "sync",
    label: "FM token rotation (days)",
    description:
      "How often the on-prem sync agent rotates its FileMaker Data API token. Default 14 (tokens expire after 15 min idle by default).",
    value: "14",
    sensitive: false,
  },
];
